import { ADMIN_PAGES, type AdminPageId, type NavigationPage } from "@/app/navigation";

export type WorkflowPageFlow = {
  receivesFrom: AdminPageId[];
  sendsTo: AdminPageId[];
};

export const WORKFLOW_PAGE_FLOWS: Record<AdminPageId, WorkflowPageFlow> = {
  "command-center": {
    receivesFrom: [
      "team-operations",
      "listing-intake",
      "livedealz-intake",
      "image-builder",
      "video-slideshow-builder",
      "content-workbench",
      "media-review",
      "final-listing-approval",
      "livedealz-review",
      "user-asset-libraries",
      "automation",
      "analytics-audit",
      "inbox",
      "platform-settings",
    ],
    sendsTo: [
      "team-operations",
      "listing-intake",
      "livedealz-intake",
      "media-review",
      "final-listing-approval",
      "livedealz-review",
      "automation",
      "analytics-audit",
    ],
  },
  "team-operations": {
    receivesFrom: [
      "command-center",
      "listing-intake",
      "livedealz-intake",
      "image-builder",
      "video-slideshow-builder",
      "content-workbench",
      "media-review",
      "livedealz-review",
      "inbox",
      "platform-settings",
    ],
    sendsTo: ["inbox", "command-center", "listing-intake", "livedealz-intake"],
  },
  "listing-intake": {
    receivesFrom: ["command-center", "suppliers-providers", "campaigns", "dealz-control"],
    sendsTo: [
      "image-builder",
      "video-slideshow-builder",
      "content-workbench",
      "team-operations",
      "user-asset-libraries",
      "media-review",
      "final-listing-approval",
      "listing-detail",
    ],
  },
  "listing-detail": {
    receivesFrom: ["listing-intake"],
    sendsTo: ["listing-intake"],
  },
  "livedealz-intake": {
    receivesFrom: ["command-center", "suppliers-providers", "campaigns", "dealz-control"],
    sendsTo: [
      "image-builder",
      "video-slideshow-builder",
      "content-workbench",
      "team-operations",
      "livedealz-review",
      "user-asset-libraries",
      "dealz-control",
    ],
  },
  "image-builder": {
    receivesFrom: ["listing-intake", "livedealz-intake", "user-asset-libraries", "content-workbench", "automation", "platform-settings"],
    sendsTo: ["media-review", "livedealz-review", "final-listing-approval", "user-asset-libraries"],
  },
  "video-slideshow-builder": {
    receivesFrom: ["listing-intake", "livedealz-intake", "image-builder", "content-workbench", "automation", "platform-settings"],
    sendsTo: ["media-review", "livedealz-review", "final-listing-approval", "user-asset-libraries"],
  },
  "content-workbench": {
    receivesFrom: ["listing-intake", "livedealz-intake", "suppliers-providers", "automation", "platform-settings"],
    sendsTo: ["video-slideshow-builder", "final-listing-approval", "livedealz-review", "user-asset-libraries"],
  },
  "media-review": {
    receivesFrom: ["command-center", "image-builder", "video-slideshow-builder", "listing-intake", "team-operations"],
    sendsTo: ["final-listing-approval", "user-asset-libraries", "image-builder", "video-slideshow-builder", "inbox", "analytics-audit"],
  },
  "final-listing-approval": {
    receivesFrom: ["command-center", "listing-intake", "media-review", "suppliers-providers", "user-asset-libraries"],
    sendsTo: ["user-asset-libraries", "inbox", "command-center", "analytics-audit", "listing-intake", "media-review"],
  },
  "asset-library-builder": {
    receivesFrom: ["campaigns", "user-asset-libraries", "listing-intake", "livedealz-intake", "media-review"],
    sendsTo: ["user-asset-libraries", "media-review", "final-listing-approval", "campaigns"],
  },
  "livedealz-review": {
    receivesFrom: ["command-center", "dealz-control", "livedealz-intake", "image-builder", "video-slideshow-builder", "content-workbench", "team-operations"],
    sendsTo: ["dealz-control", "user-asset-libraries", "inbox", "analytics-audit", "image-builder", "video-slideshow-builder", "content-workbench"],
  },
  "user-asset-libraries": {
    receivesFrom: [
      "image-builder",
      "video-slideshow-builder",
      "content-workbench",
      "media-review",
      "final-listing-approval",
      "livedealz-review",
    ],
    sendsTo: ["campaigns", "dealz-control", "image-builder", "video-slideshow-builder", "content-workbench", "analytics-audit"],
  },
  automation: {
    receivesFrom: ["command-center", "platform-settings", "roles-permissions", "listing-intake", "content-workbench", "analytics-audit", "dealz-control"],
    sendsTo: [
      "listing-intake",
      "livedealz-intake",
      "image-builder",
      "video-slideshow-builder",
      "content-workbench",
      "inbox",
      "team-operations",
      "analytics-audit",
      "platform-settings",
    ],
  },
  "analytics-audit": {
    receivesFrom: [
      "command-center",
      "team-operations",
      "listing-intake",
      "livedealz-intake",
      "media-review",
      "final-listing-approval",
      "livedealz-review",
      "user-asset-libraries",
      "automation",
      "platform-settings",
    ],
    sendsTo: ["command-center", "team-operations", "suppliers-providers", "automation", "platform-settings"],
  },
  "roles-permissions": {
    receivesFrom: ["app-shell", "inbox", "platform-settings", "automation"],
    sendsTo: ["app-shell", "automation", "inbox", "platform-settings"],
  },
  "app-shell": {
    receivesFrom: ["roles-permissions", "inbox", "platform-settings"],
    sendsTo: [
      "command-center",
      "team-operations",
      "listing-intake",
      "livedealz-intake",
      "image-builder",
      "video-slideshow-builder",
      "content-workbench",
      "media-review",
      "final-listing-approval",
      "livedealz-review",
      "user-asset-libraries",
      "automation",
      "analytics-audit",
      "inbox",
      "roles-permissions",
    ],
  },
  "suppliers-providers": {
    receivesFrom: ["analytics-audit", "roles-permissions", "platform-settings", "inbox"],
    sendsTo: ["campaigns", "listing-intake", "livedealz-intake", "analytics-audit", "roles-permissions"],
  },
  campaigns: {
    receivesFrom: ["suppliers-providers", "platform-settings", "roles-permissions", "team-operations", "user-asset-libraries", "automation"],
    sendsTo: ["dealz-control", "listing-intake", "livedealz-intake", "image-builder", "team-operations", "user-asset-libraries", "analytics-audit"],
  },
  "dealz-control": {
    receivesFrom: ["campaigns", "livedealz-intake", "livedealz-review", "automation", "team-operations", "user-asset-libraries"],
    sendsTo: ["livedealz-intake", "livedealz-review", "user-asset-libraries", "analytics-audit", "inbox"],
  },
  inbox: {
    receivesFrom: [
      "command-center",
      "team-operations",
      "media-review",
      "final-listing-approval",
      "livedealz-review",
      "automation",
      "roles-permissions",
      "dealz-control",
      "platform-settings",
    ],
    sendsTo: ["team-operations", "roles-permissions", "command-center", "listing-intake", "media-review", "final-listing-approval"],
  },
  "platform-settings": {
    receivesFrom: ["analytics-audit", "automation", "roles-permissions", "command-center"],
    sendsTo: [
      "listing-intake",
      "livedealz-intake",
      "image-builder",
      "video-slideshow-builder",
      "content-workbench",
      "media-review",
      "user-asset-libraries",
      "analytics-audit",
      "roles-permissions",
      "automation",
    ],
  },
};

const pageById = new Map<AdminPageId, NavigationPage>(ADMIN_PAGES.map((page) => [page.id, page]));

export const getWorkflowPages = (pageIds: AdminPageId[]): NavigationPage[] =>
  pageIds.map((pageId) => pageById.get(pageId)).filter((page): page is NavigationPage => Boolean(page));
